/**
 * `/campaigns` editions and audiences logic (email-newsletter-editions,
 * 2026-10-18; build-output/architecture/email-campaigns.md → "Editions" and
 * "Audiences"): what an edition's status and approval say, when this viewer
 * may approve it, when to re-read, a body as a member sees it, the edition
 * grid (each lifecycle and arm at one checkpoint), the experiment readout
 * (arm B against arm A on the edition's own metric), an edition's recipient
 * counts, and the audiences matrix. Pure: the page renders what these
 * return, and `newsletter-editions.test.ts` pins them.
 *
 * Since 2026-10-31 (email-newsletter-tag-audience) also who an edition goes
 * to by its picked tags, when an admin may change them, and the send from the
 * page: when the Send panel shows, the counts it confirms, the schedule's
 * window, and whether a send in progress may be withdrawn or stopped.
 *
 * The wire shapes mirror the outputs of `list_newsletter_editions`,
 * `get_newsletter_edition`, `preview_email_newsletter` and
 * `preview_marketing_audience`
 * (`packages/canonical-actions/src/actions/newsletter-editions-shared.ts` and
 * the two preview actions).
 */

import type { EmailCampaignCheckpointKey } from "@layer/canonical-data/email-campaigns";
import {
  NEWSLETTER_SCHEDULE_MAX_DAYS,
  NEWSLETTER_VARIANTS,
  fillNewsletterBody,
  type NewsletterApprovalVia,
  type NewsletterDecisionCheckpoint,
  type NewsletterEditionStatus,
  type NewsletterPrimaryMetric,
  type NewsletterVariant,
} from "@layer/canonical-data/email-newsletter";
import {
  MARKETING_EXCLUSION_REASONS,
  MARKETING_GOALS,
  MARKETING_LIFECYCLES,
  type MarketingChannel,
  type MarketingExclusionReason,
  type MarketingGoal,
  type MarketingLifecycle,
} from "@layer/canonical-data/marketing-audience";
import { chanceBAboveA } from "./ab-test";
import {
  checkpointCell,
  gridMetric,
  metricCount,
  type CampaignMetrics,
  type CampaignSummary,
  type CheckpointCell,
} from "./campaigns";

// ---------------------------------------------------------------------------
// Wire shapes.
// ---------------------------------------------------------------------------

export interface NewsletterEditionApproval {
  readonly version: number;
  readonly via: NewsletterApprovalVia;
  readonly by_actor: string;
  /** The approving staff member's display name (dashboard approvals). */
  readonly by_name: string | null;
  readonly gmail_message_id: string | null;
  readonly at: string;
}

/** A picked tag, its label as the tag reads now; `null` once the tag is deleted. */
export interface NewsletterEditionAudienceTag {
  readonly slug: string;
  readonly label: string | null;
}

export interface NewsletterEditionCampaignRef {
  readonly campaign_id: string;
  readonly audience_key: string;
  readonly variant_key: string;
}

export interface NewsletterEdition {
  readonly edition_id: string;
  readonly slug: string;
  readonly title: string;
  readonly topic: string | null;
  readonly hook: string | null;
  readonly status: NewsletterEditionStatus;
  readonly draft_version: number;
  readonly from_address: string;
  readonly lifecycles: ReadonlyArray<MarketingLifecycle>;
  /** The tags it goes to (members carrying ANY of them); empty = whole lifecycles. */
  readonly audience_tags: ReadonlyArray<NewsletterEditionAudienceTag>;
  readonly subject_a: string;
  readonly preview_text_a: string;
  readonly subject_b: string;
  readonly preview_text_b: string;
  readonly experiment_variable: string;
  readonly hypothesis: string;
  readonly primary_metric: NewsletterPrimaryMetric;
  readonly decision_checkpoint: NewsletterDecisionCheckpoint;
  readonly body_keys: ReadonlyArray<string>;
  readonly claims_count: number;
  readonly research_count: number;
  readonly content_sha256: string;
  readonly is_test: boolean;
  readonly approval: NewsletterEditionApproval | null;
  readonly scheduled_for: string | null;
  readonly sent_at: string | null;
  readonly audience_counts: Readonly<Record<string, number>> | null;
  readonly campaigns: ReadonlyArray<NewsletterEditionCampaignRef>;
  readonly created_by: string;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface NewsletterEditionsList {
  readonly editions: ReadonlyArray<NewsletterEdition>;
  /** Whether THIS viewer could approve on the dashboard (a staff admin). */
  readonly can_approve_on_dashboard: boolean;
}

/**
 * `get_newsletter_edition`: the edition in full. The bodies, claims and
 * research come back `null` on a replayed read (`responsePiiPaths`); the page
 * mints a fresh request each time, so it sees them.
 */
export interface NewsletterEditionDetail extends NewsletterEdition {
  readonly bodies: Readonly<Record<string, string>> | null;
  readonly claims: ReadonlyArray<{
    readonly claim: string;
    readonly source_url: string;
    readonly source_title?: string;
  }> | null;
  readonly research: ReadonlyArray<{
    readonly title: string;
    readonly url: string;
    readonly note?: string;
  }> | null;
}

export interface VariantCounts {
  readonly A: number;
  readonly B: number;
}

export interface NewsletterEditionPreview {
  readonly edition_id: string;
  readonly slug: string;
  readonly draft_version: number;
  readonly status: NewsletterEditionStatus;
  /** A test edition: its recipients are the active members tagged `testing`. */
  readonly is_test: boolean;
  readonly as_of: string;
  readonly lifecycles: ReadonlyArray<{
    readonly lifecycle: MarketingLifecycle;
    readonly recipients: number;
    readonly variants: VariantCounts;
    readonly goals: ReadonlyArray<{
      readonly goal: MarketingGoal;
      readonly recipients: number;
      readonly variants: VariantCounts;
      readonly body_key: string | null;
    }>;
  }>;
  readonly total_recipients: number;
  readonly max_recipients: number;
  readonly over_cap: boolean;
  readonly excluded: Readonly<Record<MarketingExclusionReason, number>>;
  readonly outside_lifecycles: number;
  readonly without_body: number;
  /** The tags it goes to; empty = whole lifecycles. */
  readonly audience_tag_slugs: ReadonlyArray<string>;
}

export interface MarketingAudienceCellWire {
  readonly lifecycle: MarketingLifecycle;
  readonly goal: MarketingGoal;
  readonly members: number;
  readonly email_reachable: number;
  readonly whatsapp_reachable: number;
}

export interface MarketingAudiencePreview {
  readonly as_of: string;
  readonly lapse_days: number;
  readonly cells: ReadonlyArray<MarketingAudienceCellWire>;
  readonly totals: {
    readonly members: number;
    readonly email_reachable: number;
    readonly whatsapp_reachable: number;
  };
  readonly excluded: {
    readonly email: Readonly<Record<MarketingExclusionReason, number>>;
    readonly whatsapp: Readonly<Record<MarketingExclusionReason, number>>;
  };
}

// ---------------------------------------------------------------------------
// Labels.
// ---------------------------------------------------------------------------

export const LIFECYCLE_LABEL: Readonly<Record<MarketingLifecycle, string>> = {
  member: "Members",
  trial: "Trial",
  winback: "Win-back",
  lead: "Leads",
};

export const GOAL_LABEL: Readonly<Record<MarketingGoal, string>> = {
  rehab_injury: "Rehab & injury",
  weight_loss: "Weight loss",
  toning: "Toning",
  muscle: "Muscle",
  general: "General",
};

export const PRIMARY_METRIC_LABEL: Readonly<Record<NewsletterPrimaryMetric, string>> = {
  unique_open_rate: "Unique open rate",
  unique_click_rate: "Unique click rate",
  unsubscribe_rate: "Unsubscribe rate",
};

function lifecycleLabel(key: string | null): string {
  const found = MARKETING_LIFECYCLES.find((l) => l === key);
  return found === undefined ? (key ?? "?") : LIFECYCLE_LABEL[found];
}

/** A newsletters-table row's name: an edition's row reads "<edition> · <lifecycle> · <arm>". */
export function campaignDisplayName(c: CampaignSummary): string {
  if (c.edition_id === null) return c.name;
  const edition = c.edition_title ?? c.edition_slug ?? c.name;
  return `${edition} · ${lifecycleLabel(c.audience_key)} · ${c.variant_key ?? "?"}`;
}

/** Why a member is left out, in words; `no_address` reads per channel. */
export function exclusionLabel(reason: MarketingExclusionReason, channel: MarketingChannel): string {
  switch (reason) {
    case "member_inactive":
      return "Not an active member";
    case "not_test_recipient":
      return "Not tagged testing (a test edition)";
    case "not_in_audience_tags":
      return "Not in the picked tags";
    case "internal_or_test":
      return "Staff, test or do-not-contact";
    case "marketing_opted_out":
      return "Opted out of all marketing";
    case "newsletter_opted_out":
      return "Unsubscribed from the newsletter";
    case "site_consent_withdrawn":
      return "Turned marketing off on the website";
    case "channel_opted_out":
      return channel === "email" ? "Asked not to be emailed" : "Asked not to be messaged on WhatsApp";
    case "no_address":
      return channel === "email" ? "No usable email address" : "No usable WhatsApp number";
    case "placeholder_email":
      return "Placeholder email address";
    case "bounced_or_complained":
      // deliverability-ladder (2026-10-31): a bounce keeps the address out
      // only while its pause runs; a dead account or a complaint, for good.
      return "Email bouncing, gone, or marked as spam";
    case "number_paused":
      return "WhatsApp number paused: WhatsApp said it isn't on WhatsApp";
    case "us_number":
      // deliverability-ladder D7 (2026-10-31): the studio's switch leaves US
      // numbers out of WhatsApp marketing while Meta does not deliver there.
      return "US number: Meta doesn't deliver marketing";
    case "joined_recently":
      // Only the send holds anyone back (the preview counts everyone), so this
      // never reaches the "Left out" list; worded to keep the switch exhaustive.
      return "Joined in the day before the send (gets the next one)";
    case "duplicate_number":
      return "Number shared with another member";
  }
}

/** The left-out reasons with a count, largest first (ties in the module's order). */
export function exclusionRows(
  counts: Readonly<Record<MarketingExclusionReason, number>>,
  channel: MarketingChannel,
): ReadonlyArray<{ readonly reason: MarketingExclusionReason; readonly label: string; readonly count: number }> {
  return MARKETING_EXCLUSION_REASONS.map((reason) => ({
    reason,
    label: exclusionLabel(reason, channel),
    count: counts[reason] ?? 0,
  }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
}

// ---------------------------------------------------------------------------
// Status, approval, polling.
// ---------------------------------------------------------------------------

export type EditionTone = "warn" | "ok" | "info" | "muted";

/** The status tag: its words and tone. Times are formatted by the page. */
export function editionStatusView(e: NewsletterEdition): { readonly text: string; readonly tone: EditionTone } {
  switch (e.status) {
    case "awaiting_approval":
      return { text: `v${String(e.draft_version)} awaiting approval`, tone: "warn" };
    case "approved":
      return { text: `v${String(e.draft_version)} approved`, tone: "ok" };
    case "scheduled":
      return { text: "Scheduled", tone: "info" };
    case "sending":
      return { text: "Sending", tone: "info" };
    case "sent":
      return { text: "Sent", tone: "muted" };
    case "cancelled":
      return { text: "Cancelled", tone: "muted" };
  }
}

/**
 * "v2 approved by email reply" / "v2 approved by Mario on the dashboard" /
 * "v2 approved by Eladio through his AI connector" (migration 254); `null`
 * before any approval.
 */
export function approvalText(approval: NewsletterEditionApproval | null): string | null {
  if (approval === null) return null;
  const v = `v${String(approval.version)} approved`;
  if (approval.via === "email_reply") return `${v} by email reply`;
  const where = approval.via === "connector" ? "through their AI connector" : "on the dashboard";
  return approval.by_name === null ? `${v} ${where}` : `${v} by ${approval.by_name} ${where}`;
}

/** The Approve button shows only to a viewer who may approve, on an edition awaiting it. */
export function showApproveButton(e: NewsletterEdition, canApproveOnDashboard: boolean): boolean {
  return canApproveOnDashboard && e.status === "awaiting_approval";
}

// ---------------------------------------------------------------------------
// Picked tags and the send (email-newsletter-tag-audience, 2026-10-31).
// ---------------------------------------------------------------------------

/** A picked tag's name: its label, or its slug marked deleted once the tag is gone. */
export function audienceTagName(t: NewsletterEditionAudienceTag): string {
  return t.label ?? `${t.slug} (deleted)`;
}

/**
 * Who the edition goes to: "Members, Leads" or, with tags, "Members, Leads
 * tagged OCTOBER15 openers or VIP". A test edition says so instead.
 */
export function audienceText(e: NewsletterEdition): string {
  if (e.is_test) return "Members tagged testing only";
  const lifecycles = e.lifecycles.map((l) => LIFECYCLE_LABEL[l]).join(", ");
  if (e.audience_tags.length === 0) return lifecycles;
  return `${lifecycles} tagged ${e.audience_tags.map(audienceTagName).join(" or ")}`;
}

/** The statuses an edition's tags may change in: saving a version is allowed there. */
const TAGS_EDITABLE: ReadonlySet<NewsletterEditionStatus> = new Set(["awaiting_approval", "approved"]);

/**
 * An admin may pick an edition's tags while it can still be saved; never on
 * a test edition, which goes to the `testing` tag alone.
 */
export function canPickTags(e: NewsletterEdition, canApproveOnDashboard: boolean): boolean {
  return canApproveOnDashboard && !e.is_test && TAGS_EDITABLE.has(e.status);
}

/** Whether two tag lists name the same tags, whatever their order. */
export function sameTags(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  const left = [...new Set(a)].sort();
  const right = [...new Set(b)].sort();
  return left.length === right.length && left.every((s, i) => s === right[i]);
}

/**
 * The Send panel shows to an admin on an edition approved at its current
 * version — the version the send must name. A test edition is sent from it
 * too (Mario, 2026-10-31), to the members tagged testing only.
 */
export function showSendPanel(e: NewsletterEdition, canApproveOnDashboard: boolean): boolean {
  return (
    canApproveOnDashboard &&
    e.status === "approved" &&
    e.approval !== null &&
    e.approval.version === e.draft_version
  );
}

/**
 * Whether the panel's "Sending to … now" / "Scheduled for …" notice has
 * outlived its send, as the edition's status moves from `was` to `now`: once
 * it leaves scheduled or sending (withdrawn, stopped or finished) the status
 * says what happened. Right after the send answers, the list still reads
 * `approved` — that alone does not end it.
 */
export function queuedNoticeEnds(was: NewsletterEditionStatus, now: NewsletterEditionStatus): boolean {
  const pending = (s: NewsletterEditionStatus): boolean => s === "scheduled" || s === "sending";
  if (pending(now)) return false;
  return now !== "approved" || pending(was);
}

/**
 * The counts the send confirms, per lifecycle, from the preview staff typed
 * the total of: the send re-plans and refuses if any one of them moved.
 */
export function confirmCountsFromPreview(p: NewsletterEditionPreview): Partial<Record<MarketingLifecycle, number>> {
  const out: Partial<Record<MarketingLifecycle, number>> = {};
  for (const l of p.lifecycles) out[l.lifecycle] = l.recipients;
  return out;
}

/** Why a preview cannot be sent as it stands; `null` when it can. */
export function sendBlockedReason(p: NewsletterEditionPreview): string | null {
  if (p.total_recipients === 0) return "Nobody to send it to right now.";
  if (p.over_cap) return `Over the ${p.max_recipients.toLocaleString("en-GB")} one edition may go to.`;
  return null;
}

/** Whether `typed` is the preview's total, as staff may write it (spaces or commas allowed). */
export function typedTotalMatches(typed: string, p: NewsletterEditionPreview): boolean {
  return typed.replace(/[\s,]/gu, "") === String(p.total_recipients);
}

/** The window a send may be scheduled in: from now to {@link NEWSLETTER_SCHEDULE_MAX_DAYS} days on. */
export function scheduleWindow(now: Date): { readonly min: Date; readonly max: Date } {
  return { min: now, max: new Date(now.getTime() + NEWSLETTER_SCHEDULE_MAX_DAYS * 86_400_000) };
}

/** Whether a chosen send time falls in that window. */
export function scheduleInWindow(at: Date, now: Date): boolean {
  const { min, max } = scheduleWindow(now);
  const t = at.getTime();
  return !Number.isNaN(t) && t > min.getTime() && t <= max.getTime();
}

/**
 * The emergency stop beside a send (`cancel_newsletter_send`), for an admin:
 * `withdraw` while it waits for its time (nothing has gone; it stays
 * approved), `stop` while it is sending (the next batch does not go).
 */
export function cancelKind(
  e: NewsletterEdition,
  canApproveOnDashboard: boolean,
): "withdraw" | "stop" | null {
  if (!canApproveOnDashboard) return null;
  if (e.status === "scheduled") return "withdraw";
  if (e.status === "sending") return "stop";
  return null;
}

/**
 * Withdraw edition (`withdraw_newsletter_edition`; newsletter-edition-withdraw,
 * 2026-10-31), for an admin: an edition that was never scheduled — awaiting
 * approval, or approved — retired for good. A scheduled one is first taken
 * back with the Withdraw beside its send ({@link cancelKind}), which leaves it
 * approved.
 */
export function showWithdrawEdition(e: NewsletterEdition, canApproveOnDashboard: boolean): boolean {
  return canApproveOnDashboard && (e.status === "awaiting_approval" || e.status === "approved");
}

/** How often the editions are re-read while one is going out. */
export const EDITIONS_POLL_MS = 30_000;

/** A scheduled edition is re-read from this long before its send time. */
export const EDITIONS_POLL_LEAD_MS = 15 * 60_000;

/**
 * The longest wait between two reads while an edition is scheduled. A
 * browser runs a timer of more than 2^31 − 1 ms (about 24.8 days) at once,
 * so an uncapped wait for a send that far off became a loop of reads
 * (2026-10-19 review); and an hour means a send time changed elsewhere shows
 * within the hour.
 */
export const EDITIONS_POLL_MAX_MS = 60 * 60_000;

/**
 * When to re-read the editions next, in ms from `now`; `null` when nothing
 * will move on its own. An edition's status changes without the page doing
 * anything only while it goes out: every {@link EDITIONS_POLL_MS} while one
 * is sending, or from {@link EDITIONS_POLL_LEAD_MS} before a scheduled one's
 * time — until then the wait is the time to that window, at most
 * {@link EDITIONS_POLL_MAX_MS}. A scheduled edition can sit for 14 days, and
 * each re-read is an audited dispatch, so polling it all that while would
 * write thousands of rows a day for nothing (2026-10-18 review). The page
 * re-reads only while it is visible.
 */
export function editionsPollDelay(editions: ReadonlyArray<NewsletterEdition>, now: Date): number | null {
  let delay: number | null = null;
  for (const e of editions) {
    let d: number | null = null;
    if (e.status === "sending") {
      d = EDITIONS_POLL_MS;
    } else if (e.status === "scheduled") {
      const at = e.scheduled_for === null ? Number.NaN : Date.parse(e.scheduled_for);
      d = Number.isNaN(at)
        ? EDITIONS_POLL_MS
        : Math.min(EDITIONS_POLL_MAX_MS, Math.max(EDITIONS_POLL_MS, at - EDITIONS_POLL_LEAD_MS - now.getTime()));
    }
    if (d !== null && (delay === null || d < delay)) delay = d;
  }
  return delay;
}

/**
 * The chance B is better, as a percentage. Never 100% or 0%: a comparison of
 * counts is never certain, so the ends read `>99.9%` and `<0.1%`.
 */
export function fmtChance(p: number | null): string {
  if (p === null) return "—";
  if (p > 0.999) return ">99.9%";
  if (p < 0.001) return "<0.1%";
  return `${(p * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// A body as a member sees it.
// ---------------------------------------------------------------------------

/** "Members · Weight loss" for `member:weight_loss`; the key itself when unknown. */
export function bodyKeyLabel(key: string): string {
  const [lifecycle, goal] = key.split(":");
  const l = MARKETING_LIFECYCLES.find((x) => x === lifecycle);
  const g = MARKETING_GOALS.find((x) => x === goal);
  return l === undefined || g === undefined ? key : `${LIFECYCLE_LABEL[l]} · ${GOAL_LABEL[g]}`;
}

/**
 * A body filled the way the send fills it, for a member with no first name
 * on file ("Hi there,"), arm A's preview text, and an unsubscribe link that
 * goes nowhere (`#`), so trying it in the preview unsubscribes no one. The
 * page shows it in a sandboxed frame, where no script runs.
 */
export function sampleNewsletterBody(html: string, previewText: string): string {
  return fillNewsletterBody(html, { first_name: "there", preview_text: previewText, unsubscribe_url: "#" });
}

// ---------------------------------------------------------------------------
// The edition grid and the experiment readout.
// ---------------------------------------------------------------------------

/** An edition's campaign rows (one per lifecycle and arm), by `lifecycle:arm`. */
export function editionCampaigns(
  editionId: string,
  campaigns: ReadonlyArray<CampaignSummary>,
): ReadonlyMap<string, CampaignSummary> {
  const out = new Map<string, CampaignSummary>();
  for (const c of campaigns) {
    if (c.edition_id !== editionId || c.audience_key === null || c.variant_key === null) continue;
    out.set(`${c.audience_key}:${c.variant_key}`, c);
  }
  return out;
}

/** A checkpoint's metrics once captured, else `null`. */
export function checkpointMetrics(c: CampaignSummary, key: EmailCampaignCheckpointKey): CampaignMetrics | null {
  const k = c.checkpoints.find((x) => x.checkpoint === key);
  return k !== undefined && k.captured_at !== null ? k.metrics : null;
}

export interface EditionGridRow {
  readonly lifecycle: MarketingLifecycle;
  readonly variant: NewsletterVariant;
  readonly subject: string;
  /** No campaign row: this lifecycle and arm has not gone out. */
  readonly campaign: CampaignSummary | null;
  readonly metrics: CampaignMetrics | null;
  /** Why there are no metrics yet, when there are none. */
  readonly cell: CheckpointCell;
}

/** Each lifecycle the edition goes to, arm A then arm B, at one checkpoint. */
export function editionGrid(
  e: NewsletterEdition,
  campaigns: ReadonlyArray<CampaignSummary>,
  key: EmailCampaignCheckpointKey,
  now: Date,
): ReadonlyArray<EditionGridRow> {
  const rows = editionCampaigns(e.edition_id, campaigns);
  const metric = gridMetric("clicks");
  const out: EditionGridRow[] = [];
  for (const lifecycle of MARKETING_LIFECYCLES) {
    if (!e.lifecycles.includes(lifecycle)) continue;
    for (const variant of NEWSLETTER_VARIANTS) {
      const campaign = rows.get(`${lifecycle}:${variant}`) ?? null;
      out.push({
        lifecycle,
        variant,
        subject: variant === "A" ? e.subject_a : e.subject_b,
        campaign,
        metrics: campaign === null ? null : checkpointMetrics(campaign, key),
        cell: campaign === null ? { state: "none" } : checkpointCell(campaign, key, metric, now),
      });
    }
  }
  return out;
}

/** The count each primary metric is a share of delivered for. */
const METRIC_COUNT: Readonly<Record<NewsletterPrimaryMetric, string>> = {
  unique_open_rate: "unique_opened",
  unique_click_rate: "unique_clicked",
  unsubscribe_rate: "unsubscribed",
};

/** Opens and clicks: higher is better. Unsubscribes: lower is. */
export function higherIsBetter(metric: NewsletterPrimaryMetric): boolean {
  return metric !== "unsubscribe_rate";
}

/** How sure the readout must be before it names a winner. */
export const EXPERIMENT_WIN_CHANCE = 0.95;

export interface ArmReading {
  readonly successes: number;
  readonly delivered: number;
  readonly rate: number | null;
}

export interface ExperimentComparison {
  readonly a: ArmReading;
  readonly b: ArmReading;
  /** The chance B is truly better on the metric (its direction applied); `null` without data. */
  readonly chanceBBetter: number | null;
  readonly verdict: "b_better" | "a_better" | "no_winner_yet" | "no_data";
}

function reading(successes: number, delivered: number): ArmReading {
  return { successes, delivered, rate: delivered > 0 ? successes / delivered : null };
}

function compare(metric: NewsletterPrimaryMetric, a: ArmReading, b: ArmReading): ExperimentComparison {
  if (a.delivered === 0 || b.delivered === 0) {
    return { a, b, chanceBBetter: null, verdict: "no_data" };
  }
  const above = chanceBAboveA(
    { successes: a.successes, trials: a.delivered },
    { successes: b.successes, trials: b.delivered },
  );
  if (above === null) return { a, b, chanceBBetter: null, verdict: "no_data" };
  const chance = higherIsBetter(metric) ? above : 1 - above;
  const verdict =
    chance >= EXPERIMENT_WIN_CHANCE
      ? "b_better"
      : chance <= 1 - EXPERIMENT_WIN_CHANCE
        ? "a_better"
        : "no_winner_yet";
  return { a, b, chanceBBetter: chance, verdict };
}

/**
 * An arm that went to no one, for good. Once the send is claimed (the
 * edition has campaign rows), an arm with no row had nobody in it; a
 * `not_sent` arm lost every reader at send time; a `cancelled` arm's send
 * was stopped before its batch. No checkpoint will ever come for it.
 */
function wentToNoOne(c: CampaignSummary | undefined, claimed: boolean): boolean {
  if (c === undefined) return claimed;
  return c.vendor_status === "not_sent" || c.vendor_status === "cancelled";
}

/** One arm's counts at a checkpoint, `null` until captured (or out of range). */
function armCounts(
  c: CampaignSummary | undefined,
  key: EmailCampaignCheckpointKey,
  metric: NewsletterPrimaryMetric,
): { readonly successes: number; readonly delivered: number } | null {
  if (c === undefined) return null;
  const m = checkpointMetrics(c, key);
  const delivered = metricCount(m, "delivered");
  const successes = metricCount(m, METRIC_COUNT[metric]);
  if (delivered === null || successes === null || successes > delivered) return null;
  return { successes, delivered };
}

export interface ExperimentReadout {
  readonly metric: NewsletterPrimaryMetric;
  readonly checkpoint: EmailCampaignCheckpointKey;
  /** Both arms pooled over the lifecycles where both were captured. */
  readonly pooled: ExperimentComparison;
  /** How many of the edition's lifecycles the pooled figure includes. */
  readonly lifecyclesCounted: number;
  readonly lifecyclesTotal: number;
  readonly byLifecycle: ReadonlyArray<{
    readonly lifecycle: MarketingLifecycle;
    /** `null` until both arms of this lifecycle are captured; for good when an arm went to no one. */
    readonly comparison: ExperimentComparison | null;
    /** Why there is no comparison: an arm went to no one, or a checkpoint is still to come. */
    readonly missing: "went_to_no_one" | "not_captured" | null;
  }>;
}

/**
 * Arm B against arm A on the edition's primary metric at one checkpoint:
 * each lifecycle on its own, and pooled — counts summed over the lifecycles
 * where BOTH arms were captured, so a lifecycle still waiting cannot tilt it.
 */
export function experimentReadout(
  e: NewsletterEdition,
  campaigns: ReadonlyArray<CampaignSummary>,
  key: EmailCampaignCheckpointKey,
): ExperimentReadout {
  const rows = editionCampaigns(e.edition_id, campaigns);
  const claimed = rows.size > 0;
  const metric = e.primary_metric;
  let aS = 0;
  let aD = 0;
  let bS = 0;
  let bD = 0;
  let counted = 0;
  const byLifecycle: Array<ExperimentReadout["byLifecycle"][number]> = [];
  for (const lifecycle of MARKETING_LIFECYCLES) {
    if (!e.lifecycles.includes(lifecycle)) continue;
    const campaignA = rows.get(`${lifecycle}:A`);
    const campaignB = rows.get(`${lifecycle}:B`);
    if (wentToNoOne(campaignA, claimed) || wentToNoOne(campaignB, claimed)) {
      byLifecycle.push({ lifecycle, comparison: null, missing: "went_to_no_one" });
      continue;
    }
    const a = armCounts(campaignA, key, metric);
    const b = armCounts(campaignB, key, metric);
    if (a === null || b === null) {
      byLifecycle.push({ lifecycle, comparison: null, missing: "not_captured" });
      continue;
    }
    counted += 1;
    aS += a.successes;
    aD += a.delivered;
    bS += b.successes;
    bD += b.delivered;
    byLifecycle.push({
      lifecycle,
      comparison: compare(metric, reading(a.successes, a.delivered), reading(b.successes, b.delivered)),
      missing: null,
    });
  }
  return {
    metric,
    checkpoint: key,
    pooled: compare(metric, reading(aS, aD), reading(bS, bD)),
    lifecyclesCounted: counted,
    lifecyclesTotal: byLifecycle.length,
    byLifecycle,
  };
}

const AND_LIST = new Intl.ListFormat("en-GB", { style: "long", type: "conjunction" });

/**
 * What the pooled figure leaves out, as a sentence; `null` when it counts
 * every lifecycle. An arm that went to no one is final, so it is named apart
 * from a checkpoint still to come.
 */
export function poolNote(r: ExperimentReadout): string | null {
  if (r.lifecyclesCounted >= r.lifecyclesTotal) return null;
  const noOne = r.byLifecycle
    .filter((l) => l.missing === "went_to_no_one")
    .map((l) => LIFECYCLE_LABEL[l.lifecycle]);
  const waiting = r.byLifecycle.filter((l) => l.missing === "not_captured").length;
  const parts: string[] = [];
  if (noOne.length > 0) parts.push(`in ${AND_LIST.format(noOne)} an arm went to no one`);
  if (waiting > 0) parts.push(waiting === 1 ? "one is not captured yet" : `${String(waiting)} are not captured yet`);
  return `Pooled over ${String(r.lifecyclesCounted)} of ${String(r.lifecyclesTotal)} lifecycles: ${parts.join("; ")}.`;
}

/** The readout's sentence for one comparison. */
export function verdictText(c: ExperimentComparison): string {
  switch (c.verdict) {
    case "no_data":
      return "No numbers yet.";
    case "b_better":
      return "B is better.";
    case "a_better":
      return "A is better.";
    case "no_winner_yet":
      return "No clear winner yet.";
  }
}

// ---------------------------------------------------------------------------
// The audiences matrix.
// ---------------------------------------------------------------------------

export interface AudienceMatrixRow {
  readonly lifecycle: MarketingLifecycle;
  readonly cells: ReadonlyArray<MarketingAudienceCellWire>;
  readonly total: { readonly members: number; readonly email: number; readonly whatsapp: number };
}

/** Lifecycle rows × goal columns, every pair present (a missing cell is zeros). */
export function audienceMatrix(p: MarketingAudiencePreview): {
  readonly rows: ReadonlyArray<AudienceMatrixRow>;
  readonly goalTotals: ReadonlyArray<{ readonly goal: MarketingGoal; readonly members: number; readonly email: number; readonly whatsapp: number }>;
} {
  const at = (lifecycle: MarketingLifecycle, goal: MarketingGoal): MarketingAudienceCellWire =>
    p.cells.find((c) => c.lifecycle === lifecycle && c.goal === goal) ?? {
      lifecycle,
      goal,
      members: 0,
      email_reachable: 0,
      whatsapp_reachable: 0,
    };
  const rows = MARKETING_LIFECYCLES.map((lifecycle) => {
    const cells = MARKETING_GOALS.map((goal) => at(lifecycle, goal));
    return {
      lifecycle,
      cells,
      total: {
        members: cells.reduce((s, c) => s + c.members, 0),
        email: cells.reduce((s, c) => s + c.email_reachable, 0),
        whatsapp: cells.reduce((s, c) => s + c.whatsapp_reachable, 0),
      },
    };
  });
  const goalTotals = MARKETING_GOALS.map((goal) => {
    const cells = MARKETING_LIFECYCLES.map((lifecycle) => at(lifecycle, goal));
    return {
      goal,
      members: cells.reduce((s, c) => s + c.members, 0),
      email: cells.reduce((s, c) => s + c.email_reachable, 0),
      whatsapp: cells.reduce((s, c) => s + c.whatsapp_reachable, 0),
    };
  });
  return { rows, goalTotals };
}

/** Above this share of active members with no specific goal, the page asks for consultation forms. */
export const GOAL_DATA_THIN_SHARE = 0.5;

/** True while most active members sit in the "general" column for want of a goal on file. */
export function goalDataThin(p: MarketingAudiencePreview): boolean {
  if (p.totals.members === 0) return false;
  const general = p.cells.filter((c) => c.goal === "general").reduce((s, c) => s + c.members, 0);
  return general / p.totals.members > GOAL_DATA_THIN_SHARE;
}

// ---------------------------------------------------------------------------
// An edition's recipients, counted.
// ---------------------------------------------------------------------------

/** Which body a goal's members get, in words. */
export function bodyText(goal: MarketingGoal, lifecycle: MarketingLifecycle, bodyKey: string | null): string {
  if (bodyKey === null) return "No body: left out";
  return bodyKey === `${lifecycle}:${goal}` ? "Its own body" : `Falls back to ${bodyKey}`;
}
